<template>
	<div ref="slider" class="category-filter-range" />
</template>

<script lang="ts" setup>
import type {RangeFacet, TextFacet} from "@apptus/esales-api";
import {useVModel} from "@vueuse/core";
import * as noUiSlider from "nouislider";

const properties = defineProps<{
	facet: RangeFacet | TextFacet;
	modelValue?: (string | number | boolean)[];
	source?: string;
}>();

const emit = defineEmits<{
	(event: "update:modelValue", value?: (string | number | boolean)[]): void;
}>();

const {t} = useI18n();

const slider = ref<HTMLDivElement>();

const rangePoints = computed<number[]>(() => {
	const points: number[] = [];

	if (properties.facet.type === "RANGE") {
		points.push(0);

		if (properties.facet.unit === "cm" || properties.facet.unit === "kg") {
			const max = Math.ceil((properties.facet.max || 10) / 10) * 10;

			for (let amount = 10; amount <= max; amount += 10) {
				points.push(amount);
			}
		} else {
			const max = Math.ceil((properties.facet.max || 10_000) / 1000) * 1000;

			for (let amount = 100; amount < 1000; amount += 100) {
				points.push(amount);
			}

			for (let amount = 1000; amount <= 2000; amount += 200) {
				points.push(amount);
			}

			for (let amount = 2000; amount <= max; amount += 500) {
				points.push(amount);
			}
		}
	} else if (properties.facet.type === "TEXT") {
		const numbers = properties.facet.values
			.map((value) => Number(value.id))
			.sort((a, b) => a - b);
		points.push(...numbers);
	}

	return points;
});

const suffix = computed(() => {
	if (properties.facet.id.includes("custom.delivery_estimate_days")) {
		return ` ${t("days")}`;
	}
	if (properties.facet.type === "TEXT") {
		return " cm";
	}

	return ` ${properties.facet.unit || "€"}`;
});

const selectedItems = useVModel(properties, "modelValue", emit);

const selectedMin = computed<number | undefined>(() => {
	if (typeof selectedItems.value?.[0] === "string") {
		return Number(selectedItems.value[0]);
	}

	if (typeof selectedItems.value?.[0] === "number") {
		return selectedItems.value[0];
	}

	return undefined;
});

const selectedMax = computed<number | undefined>(() => {
	const lastValue = selectedItems.value
		? selectedItems.value.at(-1)
		: undefined;

	if (typeof lastValue === "string") {
		return Number(lastValue);
	}

	if (typeof lastValue === "number") {
		return lastValue;
	}

	return undefined;
});

const getRange = () => {
	const min = rangePoints.value[0] ?? 0;
	const max = rangePoints.value.at(-1) ?? 0;

	return {
		min,
		max,
		...Object.fromEntries(
			rangePoints.value
				.slice(1, -1)
				.map((value) => {
					const normalizedValue = value - min;
					const interval = max - min;

					return [`${Math.round((normalizedValue / interval) * 100)}%`, value];
				})
				.filter(
					([key], index, array) =>
						key !== "0%" &&
						key !== "100%" &&
						array.findIndex(([itemKey]) => itemKey === key) === index,
				),
		),
	};
};

onMounted(() => {
	if (!slider.value) {
		return;
	}

	const instance = noUiSlider.create(slider.value, {
		tooltips: true,
		format: {
			to: (value) => value.toLocaleString() + suffix.value,
			from: Number,
		},
		connect: true,
		start: [
			selectedMin.value ?? rangePoints.value[0] ?? 0,
			selectedMax.value ?? rangePoints.value.at(-1) ?? 0,
		],
		handleAttributes: [
			{
				"aria-label": `${t("value-min")} ${selectedMin.value ?? rangePoints.value[0]}`,
			},

			{
				"aria-label": `${t("value-max")} ${selectedMax.value ?? rangePoints.value.at(-1) ?? 0}`,
			},
		],
		range: getRange(),
		snap: true,
	});

	instance.on("change", (values, handle, unencoded) => {
		const [min, max] = unencoded;

		if (min === undefined || max === undefined) {
			return;
		}

		const minRange = rangePoints.value[0];
		const maxRange = rangePoints.value.at(-1);

		if (properties.facet.type === "RANGE") {
			selectedItems.value =
				min === minRange && max === maxRange ? undefined : unencoded;
		} else if (properties.facet.type === "TEXT") {
			selectedItems.value =
				min === minRange && max === maxRange
					? undefined
					: rangePoints.value.filter(
							(number) => number >= min && number <= max,
						);
		}
	});

	watch(
		() => properties.modelValue,
		(value, oldValue) => {
			if (value === oldValue) {
				return;
			}
			if (value !== oldValue) {
				instance.updateOptions(
					{
						start: [
							selectedMin.value ?? rangePoints.value[0] ?? 0,
							selectedMax.value ?? rangePoints.value.at(-1) ?? 0,
						],
					},
					true,
				);
			}

			/** This will reset the `nouislider` instance if the modelValue is set to undefined.
			 *  This is needed to reset the slider when the user sets the slider either to starting or ending values.
			 */
			if (value === undefined) {
				instance.reset();
			}
		},
		{deep: true},
	);
});
</script>

<style>
@reference "~/assets/css/tailwind.css";

/* This class is added to the element noUiSlider.create() is called on.
 * Has border, background and other styling properties to establish the slider design and background. */
.category-filter-range.noUi-target {
	@apply bg-gray-100 rounded-full relative px-3 h-6 mb-7 shadow-inner;
}

/* The slider bar. Serves as the calculating point for the slider handles, and has no visible styling. */
.category-filter-range .noUi-base {
	@apply flex items-center;
}

/* The actual, visible handles. */
.category-filter-range .noUi-handle {
	@apply rounded-full w-4 h-4 absolute -right-2 top-1 bg-white shadow cursor-ew-resize;
	backface-visibility: hidden;
}

/* An empty div within .noUi-handle. */
.category-filter-range .noUi-touch-area {
	@apply w-8 h-8 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2;
}

.category-filter-range .noUi-base,
.category-filter-range .noUi-connects {
	@apply w-full h-full relative;
}

.category-filter-range .noUi-connects {
	@apply overflow-hidden h-3;
}

/* Setting properties related to the slider connect segment. */
.category-filter-range .noUi-connect {
	@apply bg-red-600 my-px;
}

.category-filter-range .noUi-connect,
.category-filter-range .noUi-origin {
	@apply absolute top-0 right-0 h-full w-full origin-top-left will-change-transform;
}

/* The elements connected to the base, defining the handle locations. */
.category-filter-range .noUi-origin {
	@apply h-0;
}

/* .noUi-state-tap is added when the slider bar is tapped or clicked.
 * A slider with this class will reject any user input. noUiSlider will remove this
 * class after 300ms, leaving that timespan to perform visual animations. */
.category-filter-range .noUi-state-tap .noUi-connect,
.category-filter-range .noUi-state-tap .noUi-origin {
	@apply transition-transform duration-300;
}

.category-filter-range .noUi-tooltip {
	@apply whitespace-nowrap absolute border bg-white rounded px-1;
	@apply text-sm mt-2;
}

.category-filter-range .noUi-handle-lower .noUi-tooltip {
	@apply top-full left-0;
}

.category-filter-range .noUi-handle-upper .noUi-tooltip {
	@apply top-full right-0;
}

.category-filter-range .noUi[disabled] {
}
</style>

<i18n lang="json">
{
	"et": {
		"days": "päeva",
		"value-min": "Minimaalne väärtus",
		"value-max": "Maksimaalne väärtus"
	},
	"fi": {
		"days": "päivää",
		"value-min": "Minimiarvo",
		"value-max": "Maksimiarvo"
	}
}
</i18n>
